import "./App.css";

import { useEffect, useRef, useState } from "react";

import imgUrl from "./bg.webp";
import Button from "./Button";
import IconBar from "./IconBar";
import Screen from "./Screen";
import {
  readWasmStateFromIndexedDB,
  readWasmStateFromLocalStorage,
  saveWasmStateToIndexedDB,
  saveWasmStateToLocalStorage,
  type SavedState,
} from "./storageUtils";
import { useBeep } from "./useBeep";
import TamaWorker from "./worker?worker";
let worker: Worker | undefined;

export const SCREEN_WIDTH = 32;

function postMessageToWorker(data: unknown) {
  if (worker != null) {
    worker.postMessage(data);
  }
}

// Persistence lives here on the main thread (Web Workers can't touch
// localStorage). We keep two copies — IndexedDB (primary) and a localStorage
// mirror that survives the storage eviction iOS home-screen PWAs are prone to —
// and on load we restore whichever is newer.
function persistState(data: number[], savedAt: number) {
  saveWasmStateToLocalStorage(data, savedAt);
  void saveWasmStateToIndexedDB(data, savedAt).catch((e: unknown) => {
    console.error("Error saving to IndexedDB: ", e);
  });
}

async function loadNewerState(): Promise<SavedState | undefined> {
  let fromIdb: SavedState | undefined;
  try {
    fromIdb = await readWasmStateFromIndexedDB();
  } catch (e) {
    console.error("Error reading IndexedDB: ", e);
  }
  const fromLs = readWasmStateFromLocalStorage();

  if (fromIdb != null && fromLs != null) {
    return fromIdb.savedAt >= fromLs.savedAt ? fromIdb : fromLs;
  }
  return fromIdb ?? fromLs;
}

function App() {
  const [screenMatrix, setScreenMatrix] = useState<number[] | undefined>();
  const [icons, setIcons] = useState<boolean[]>(new Array(8).fill(false));
  // null = not catching up; 0..1 = fast-forwarding through time spent away.
  const [wakeProgress, setWakeProgress] = useState<number | null>(null);
  const [startBeep, stopBeep] = useBeep();

  const isMuted = useRef<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    if (worker == null) {
      worker = new TamaWorker();
      worker.addEventListener("message", ({ data }) => {
        // State to persist, forwarded from the worker.
        if (
          data != null &&
          typeof data === "object" &&
          (data as { channel?: string }).channel === "persist"
        ) {
          const { data: cpuState, savedAt } = data as {
            data: number[];
            savedAt: number;
          };
          persistState(cpuState, savedAt);
          return;
        }

        // Catch-up lifecycle: show a "waking up" indicator while the worker
        // fast-forwards through the time the pet spent unattended.
        if (
          data != null &&
          typeof data === "object" &&
          (data as { channel?: string }).channel === "status"
        ) {
          const status = data as { state: string; progress?: number };
          if (status.state === "catchup") {
            setWakeProgress(status.progress ?? 0);
          } else if (status.state === "ready") {
            setWakeProgress(null);
          }
          return;
        }

        if (Array.isArray(data)) {
          if (data.length == 8) {
            setIcons(data.map((icon) => icon === 1));
          } else {
            setScreenMatrix(data);
          }
        } else if (Number.isFinite(data)) {
          if (data === -1) {
            stopBeep();
          } else if (!isMuted.current) {
            startBeep(data as number);
          }
        }
      });

      // Load the saved game (if any), hand it to the worker, then start. The
      // worker waits for this restore decision before running, so autosave can
      // never overwrite a real save with a fresh egg.
      void loadNewerState().then((state) => {
        if (cancelled) {
          return;
        }
        postMessageToWorker({ type: "restore", state: state ?? null });
        postMessageToWorker("start");
      });
    }

    // Force a save whenever the page is backgrounded or closed, so the latest
    // state (and an accurate "saved at" timestamp) is always captured for the
    // offline catch-up on next open. On mobile, pagehide/visibilitychange are
    // the only reliable "app is going away" signals.
    const saveNow = () => postMessageToWorker("save");
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveNow();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", saveNow);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", saveNow);
    };
  }, [startBeep, stopBeep]);

  return (
    <div className="flex w-[330px] flex-col items-center gap-2 select-none">
      <div className="relative flex h-[312px] w-full flex-col bg-no-repeat">
        <IconBar
          icons={[0, 1, 2, 3]}
          status={[0, 1, 2, 3].map((icon) => icons[icon])}
        />
        {screenMatrix && <Screen matrix={screenMatrix} />}
        {wakeProgress !== null && (
          <div
            className={`
              absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 flex-col
              items-center gap-1 bg-black/70 py-3 text-white
            `}
          >
            <span className="text-sm">⏰ Waking up…</span>
            <span className="text-xs opacity-80">
              {Math.round(wakeProgress * 100)}%
            </span>
          </div>
        )}
        <IconBar
          icons={[4, 5, 6, 7]}
          status={[4, 5, 6, 7].map((icon) => icons[icon])}
        />
        <div
          className={`
            absolute inset-0 -z-10 bg-cover bg-center bg-no-repeat opacity-30
          `}
          style={{
            backgroundImage: `url(${imgUrl})`,
          }}
        ></div>
      </div>
      <div className="flex justify-center gap-10">
        <Button label="A" toucButton={postMessageToWorker} />
        <Button label="B" toucButton={postMessageToWorker} />
        <Button label="C" toucButton={postMessageToWorker} />
      </div>

      <div className="flex w-full">
        <button
          type="button"
          className="flex-grow border-1"
          onClick={() => {
            isMuted.current = !isMuted.current;
          }}
        >
          {isMuted.current ? "Unmute" : "Mute"}
        </button>
        <a
          href="https://tamaplanet.com/images/docs/Tama_Manuals/p1.pdf"
          target="_blank"
          rel="noreferrer noopener"
          className={"flex-grow border-1 text-center"}
        >
          Manual
        </a>
      </div>
    </div>
  );
}

export default App;
